import { SeriesInstanceUid, seriesInstanceUidOf } from '~/domain/dicom/uids';
import { Box } from '@mui/material';
import {
    ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { sameElements } from '~/utils/array';
import { usePlaneCycleShortcut } from '~/viewportGrid/grid/usePlaneCycleShortcut';
import {
    BlendAdapter,
    DatasetAdapter,
    EnvironmentAdapter,
    ImageDetailsContext,
    ImageRowsRequest,
    RowOverlayContext,
    PrimaryImageControls,
    RenderAdapter,
    TileOverlayContext,
    TileStatusContext,
    WindowingAdapter,
} from '../adapters/types';
import {
    DEFAULT_ROW_TEMPLATE,
    EMPTY_ROW,
    HangingProtocol,
    protocolOfLayout,
    sideBySide,
} from '../binding/hanging';
import {
    DatasetBinding,
    useDatasetBinding,
} from '../binding/useDatasetBinding';
import {
    buildLayout,
    hostRowIndex,
    imagesOfRows,
    isPanelCell,
    isSideBySide,
    leadCellOf,
    locateViewport,
    planeOfCell,
    rowCanRotate,
    templateOfRow,
} from '../layout/layout';
import {
    insertRow,
    PanelPlacementPlan,
    placePanels,
    rehangRow,
    rehangRows,
    removeRow,
    reorderViewport,
    rotateRowPlanes,
    setRowFusion,
    setRowLock,
    unbindRow,
    unbindUnavailableDatasets,
} from '../layout/mutations';
import {
    GridRowSpec,
    isPanelKind,
    Layout,
    Plane,
    PRIMARY_ROW_INDEX,
    RowFusion,
    RowLock,
    RowTemplate,
    ViewportId,
} from '../layout/types';
import { useWindowLevelFanout } from '../linking/useWindowLevelFanout';
import { GridPanel } from './GridPanel';
import { GridWidget } from './GridWidget';
import {
    ONE_PLANE_ROW_MESSAGE_ID,
    PlaneCycleOutcome,
    planeCycleTarget,
    PlaneCycleTarget,
    refusedWith,
} from '../layout/planeCycle';
import { primaryRowNoticeId } from './primaryRowNoticeId';
import type {
    RoiSelectionHandler,
    TileStructureActions,
} from '../tile/tileViewModel';
import ViewportGrid from './ViewportGrid';

interface ViewportGridHostProps {
    datasetAdapter: DatasetAdapter;
    environmentAdapter: EnvironmentAdapter;
    /** Per-series window/level truth (the engine bus, or a test double). */
    windowingAdapter: WindowingAdapter;
    /** Per-row secondary-image blend truth. */
    blendAdapter: BlendAdapter;
    rows: ReadonlyArray<GridRowSpec>;
    /** Per-tile canvas overlay, forwarded to the grid (the engine overlays of a tile). */
    tileOverlay?: (context: TileOverlayContext) => ReactNode;
    /** Per-tile title-bar status, forwarded to the grid. */
    tileStatus?: (context: TileStatusContext) => ReactNode;
    /**
     * Hangs the details of the image a tile names on its name in the title
     * bar; the name alone when absent.
     */
    imageDetails?: (context: ImageDetailsContext) => ReactNode;
    /**
     * The controls of what the host draws over a row's image, at the far
     * end of the row's footer, before the row's shape; nothing when absent.
     */
    rowOverlayControls?: (context: RowOverlayContext) => ReactNode;
    /**
     * The panels hung as tiles, in this order, each in a column of the band
     * of the row showing its host series, else of the primary row. A panel
     * that leaves the list leaves the grid; one that joins it is hung.
     */
    panels?: ReadonlyArray<GridPanel>;
    /**
     * Floated over their rows, in the order they open in. A widget that leaves
     * the list closes; one that joins it opens.
     */
    widgets?: ReadonlyArray<GridWidget>;
    /**
     * The primary image's series: the dataset the sidebar names. The primary
     * row shows it; a change rebinds that row, panes kept. When it goes
     * `undefined`, the primary row empties. When the row cannot show it, the
     * row says so.
     */
    primarySeriesUid?: SeriesInstanceUid;
    /**
     * The primary image as the owner controls it. The primary row changes
     * its image only through it, from its footer or, while empty, from its
     * picker, and offers its actions on the image it shows.
     */
    primaryImage: PrimaryImageControls;
    /**
     * The active structure set's owning dataset series. Tiles of this series
     * form the editable set: the tiles a stroke may write into.
     */
    editableSeriesUid?: SeriesInstanceUid;
    /** Whether a tile click should pick the ROI under it (pan mode only). */
    canSelectRoi?: boolean;
    /** Selects the ROI a click resolves on an editable tile; `focus` on double-click. */
    onSelectRoi?: RoiSelectionHandler;
    /** What an editable tile's hover label offers on the structure under it. */
    structureActions?: TileStructureActions;
    /**
     * The shape the owner asks every bound row to hang. A change re-hangs
     * the rows that are not in it yet; `undefined` asks for nothing. The
     * supplied rows already carry the first value, so only a later change
     * touches the layout.
     */
    hangingProtocol?: HangingProtocol;
    /**
     * Fires with the protocol every bound row hangs, and with `undefined`
     * once a row is shown apart, rotated, or otherwise hung its own way.
     * Fires on mount, then on each change. Must be a stable callback.
     */
    onHangingChanged?: (protocol: HangingProtocol | undefined) => void;
    /**
     * The images the owner asks to see in a row each, as `showImageRows`
     * shows them: rows of other images close, the primary row stays. Each
     * new request object applies once; the one the grid mounts with does
     * not apply, since the supplied rows already stand.
     */
    imageRowsRequest?: ImageRowsRequest;
    /**
     * Fires with the images the bound rows show, in row order, each once:
     * on mount, then on each change. Must be a stable callback.
     */
    onRowImagesChanged?: (
        seriesInstanceUids: ReadonlyArray<SeriesInstanceUid>
    ) => void;
    /**
     * Fires on each activation change with the active tile's renderer and
     * whether a pane is activated at all: (renderer, true) for a pane that
     * draws an image, (undefined, true) for an activated pane that draws
     * none, and (undefined, false) when nothing is activated or the grid
     * unmounts. Must be a stable callback.
     */
    onActiveRendererChanged?: (
        renderer: RenderAdapter | undefined,
        paneActivated: boolean
    ) => void;
    /**
     * Whether a tool action is in progress (a half-drawn line). A plane
     * cycle then does nothing, from the key or the row's control: it would
     * remount the views under the action. Must be a stable callback.
     */
    isActionInProgress?: () => boolean;
    /** Whether the image tiles draw their scale bar. */
    scaleBarVisible?: boolean;
}

/** An unbound row; the picker binds a dataset to it. */
const PLACEHOLDER_ROW: GridRowSpec = {
    dataset: undefined,
    template: EMPTY_ROW,
};

const NO_PANELS: ReadonlyArray<GridPanel> = [];
const NO_WIDGETS: ReadonlyArray<GridWidget> = [];

/** The in-progress guard of a host that is told of no tool action. */
function noActionInProgress(): boolean {
    return false;
}

const CYCLED: PlaneCycleOutcome = { kind: 'cycled' };
const IGNORED: PlaneCycleOutcome = { kind: 'ignored' };

/**
 * The updater made safe to replay: React can call a queued updater again
 * against the same previous layout (after it skipped a lower-priority
 * update, or under StrictMode), and it then gets back the first result. An
 * updater that mints pane ids so mints them once per previous layout, and a
 * replay never remounts a row.
 */
function replaySafe(
    update: (previous: Layout) => Layout
): (previous: Layout) => Layout {
    let last: { previous: Layout; next: Layout } | undefined;
    return (previous) => {
        if (last?.previous !== previous) {
            last = { previous, next: update(previous) };
        }
        return last.next;
    };
}

/**
 * The plan that hangs each panel in the band of the first row showing its
 * host series, else the primary row's, one entry per panel in list order.
 */
function panelPlacementPlan(
    layout: Layout,
    panels: ReadonlyArray<Pick<GridPanel, 'kind' | 'hostSeriesUid'>>
): PanelPlacementPlan {
    return panels.map(({ kind, hostSeriesUid }) => ({
        rowIndex: hostRowIndex(layout, hostSeriesUid),
        kinds: [kind],
    }));
}

/**
 * Adapter-driven grid host: owns the layout + active/maximize state and wires
 * the grid actions (resize / add row / close row / maximize / bind /
 * side by side / plane cycle) to the pure reducers + the binding controller.
 * Presentation only. The mounting composition root supplies the adapters and
 * owns every engine effect (input, arming, flush, bridges). A new row opens
 * as an empty placeholder, bound via the picker. R and Ctrl+R cycle the
 * planes of one row, the one {@link planeCycleTarget} names, as the row's
 * plane control does.
 */
function ViewportGridHost({
    datasetAdapter,
    environmentAdapter,
    windowingAdapter,
    blendAdapter,
    rows,
    tileOverlay,
    tileStatus,
    imageDetails,
    rowOverlayControls,
    panels = NO_PANELS,
    widgets = NO_WIDGETS,
    primarySeriesUid,
    primaryImage,
    editableSeriesUid,
    canSelectRoi,
    onSelectRoi,
    structureActions,
    hangingProtocol,
    onHangingChanged,
    imageRowsRequest,
    onRowImagesChanged,
    onActiveRendererChanged,
    isActionInProgress = noActionInProgress,
    scaleBarVisible = false,
}: ViewportGridHostProps) {
    const idCounter = useRef(0);
    const makeId = useCallback(
        () => `vp-${(idCounter.current += 1)}` as ViewportId,
        []
    );

    const [layout, setLayoutState] = useState<Layout>(() =>
        buildLayout(rows, makeId)
    );
    const setLayout = useCallback(
        (update: (previous: Layout) => Layout) =>
            setLayoutState(replaySafe(update)),
        []
    );
    const [activeId, setActiveId] = useState<ViewportId | undefined>(undefined);
    // The unbound row whose pane is activated. It draws no image, so while
    // it is active no tile is: the tools never act on a tile of another row.
    const [activeUnboundRow, setActiveUnboundRow] = useState<
        number | undefined
    >(undefined);
    const activatePane = useCallback((id: ViewportId) => {
        setActiveId(id);
        setActiveUnboundRow(undefined);
    }, []);
    const activateUnboundRow = useCallback((rowIndex: number) => {
        setActiveId(undefined);
        setActiveUnboundRow(rowIndex);
    }, []);
    const [maximizedId, setMaximizedId] = useState<ViewportId | undefined>(
        undefined
    );
    // The shape a row had before it was shown apart, keyed by the pane the
    // re-hang keeps: the memory dies with that pane, and two rows of one
    // dataset never share an entry.
    const rememberedShapes = useRef(new Map<ViewportId, RowTemplate>());

    const handleToggleMaximize = (id: ViewportId) => {
        setMaximizedId((current) => (current === id ? undefined : id));
    };

    const handleAddRowBelow = (rowIndex: number) => {
        setLayout((previous) =>
            insertRow(previous, rowIndex + 1, PLACEHOLDER_ROW, makeId)
        );
    };

    const handleCloseRow = (rowIndex: number) => {
        const row = layout.rows[rowIndex];
        const kept = row ? leadCellOf(row) : undefined;
        if (kept) {
            rememberedShapes.current.delete(kept.id);
        }
        setLayout((previous) => removeRow(previous, rowIndex));
    };

    // The one plane cycle of a row, which the key and the row's control
    // both run, so the two always give one result.
    const cycleRowPlanes = useCallback(
        (target: PlaneCycleTarget): PlaneCycleOutcome => {
            if (isActionInProgress()) {
                return IGNORED;
            }
            if (target.kind === 'refused') {
                return target;
            }
            const { rowIndex } = target;
            const row = layout.rows[rowIndex];
            if (!row || !rowCanRotate(row)) {
                return refusedWith(ONE_PLANE_ROW_MESSAGE_ID);
            }
            setLayout((previous) => rotateRowPlanes(previous, rowIndex));
            return CYCLED;
        },
        [isActionInProgress, layout, setLayout]
    );
    const handleRotateRowPlanes = (rowIndex: number) => {
        cycleRowPlanes({ kind: 'row', rowIndex });
    };

    // A shape picked for the row is the row's own choice, so the shape kept
    // from before a side-by-side is not brought back over it.
    const handleRehangRow = (rowIndex: number, template: RowTemplate) => {
        const row = layout.rows[rowIndex];
        const kept = row ? leadCellOf(row) : undefined;
        if (kept) {
            rememberedShapes.current.delete(kept.id);
        }
        setLayout((previous) =>
            rehangRow(previous, rowIndex, template, makeId)
        );
    };

    const handleSetRowLock = (rowIndex: number, lock: RowLock) => {
        setLayout((previous) => setRowLock(previous, rowIndex, lock));
    };

    const handleSetRowFusion = (
        rowIndex: number,
        fusion: RowFusion | undefined
    ) => {
        setLayout((previous) => setRowFusion(previous, rowIndex, fusion));
    };

    const handleReorder = (id: ViewportId, toColumnIndex: number) => {
        setLayout((previous) => reorderViewport(previous, id, toColumnIndex));
    };

    // A row hung apart from the layout menu has no remembered shape and
    // returns to the default. The pair hangs on the kept pane's own plane.
    const handleToggleSideBySide = (rowIndex: number) => {
        const row = layout.rows[rowIndex];
        const kept = row ? leadCellOf(row) : undefined;
        if (!row?.dataset || !kept) {
            return;
        }
        const remembered = rememberedShapes.current;
        if (isSideBySide(row)) {
            const template = remembered.get(kept.id) ?? DEFAULT_ROW_TEMPLATE;
            remembered.delete(kept.id);
            setLayout((previous) =>
                rehangRow(previous, rowIndex, template, makeId)
            );
            return;
        }
        remembered.set(kept.id, templateOfRow(row));
        const plane = planeOfCell(kept) ?? Plane.Transversal;
        setLayout((previous) =>
            rehangRow(previous, rowIndex, sideBySide(plane), makeId)
        );
    };

    const windowLevelTargets = useWindowLevelFanout(layout, datasetAdapter);
    const binding = useDatasetBinding(setLayout, datasetAdapter, makeId);

    // The primary row follows the primary image, so the sidebar and the grid
    // never disagree about what is being worked on. A change rebinds that
    // row, panes kept; every other row is the user's own and is never
    // touched. A primary image that registers after the sidebar names it is
    // bound when it arrives. A bind the grid already asked for is not asked
    // again when other datasets come and go, so a row the user rebound keeps
    // the user's image; a primary image that leaves the grid and registers
    // again is bound again. A primary image the sidebar lets go empties the
    // row. Layout and binder are read through refs, so only the primary
    // image or the datasets changing re-run the effect. It runs before
    // paint, so a change never shows the row apart from the sidebar.
    const { availableDatasets } = binding;
    const layoutRef = useRef(layout);
    layoutRef.current = layout;
    const requestBindRef = useRef(binding.requestBind);
    requestBindRef.current = binding.requestBind;
    const previousPrimaryRef = useRef(primarySeriesUid);
    const requestedPrimaryRef = useRef<string | undefined>(undefined);
    useLayoutEffect(() => {
        const previous = previousPrimaryRef.current;
        previousPrimaryRef.current = primarySeriesUid;
        if (primarySeriesUid === undefined) {
            requestedPrimaryRef.current = undefined;
            if (previous !== undefined) {
                setLayout((current) => unbindRow(current, PRIMARY_ROW_INDEX));
            }
            return;
        }
        if (datasetAdapter.resolve(primarySeriesUid) === undefined) {
            requestedPrimaryRef.current = undefined;
            return;
        }
        const primaryRow = layoutRef.current.rows[PRIMARY_ROW_INDEX];
        if (primaryRow?.dataset?.seriesInstanceUid === primarySeriesUid) {
            requestedPrimaryRef.current = primarySeriesUid;
            return;
        }
        if (requestedPrimaryRef.current === primarySeriesUid) {
            return;
        }
        requestedPrimaryRef.current = primarySeriesUid;
        requestBindRef.current(PRIMARY_ROW_INDEX, primarySeriesUid);
    }, [primarySeriesUid, availableDatasets, datasetAdapter, setLayout]);

    const intl = useIntl();
    const noticeId = primaryRowNoticeId(
        layout.rows[PRIMARY_ROW_INDEX],
        primarySeriesUid
    );
    const notice = noticeId ? intl.formatMessage({ id: noticeId }) : undefined;

    // A dataset that leaves the adapter, as a deleted image does, leaves
    // every row that shows it: the row goes back to a placeholder, so no
    // tile draws an image the patient no longer holds.
    useEffect(() => {
        setLayout((previous) =>
            unbindUnavailableDatasets(
                previous,
                (seriesInstanceUid) =>
                    datasetAdapter.resolve(seriesInstanceUid) !== undefined
            )
        );
    }, [availableDatasets, datasetAdapter, setLayout]);

    // A pick in an empty row's image menu goes with the menu when the row
    // binds, so focus moves on to the row's first tile rather than fall to
    // the page. The move is due on the next change of the layout.
    const rootRef = useRef<HTMLDivElement>(null);
    const focusOnBindRef = useRef<number | undefined>(undefined);
    const rowBinding: DatasetBinding = {
        ...binding,
        requestBind: (rowIndex, seriesInstanceUid) => {
            focusOnBindRef.current = layoutRef.current.rows[rowIndex]?.dataset
                ? undefined
                : rowIndex;
            binding.requestBind(rowIndex, seriesInstanceUid);
        },
    };
    useEffect(() => {
        const rowIndex = focusOnBindRef.current;
        focusOnBindRef.current = undefined;
        const row = rowIndex === undefined ? undefined : layout.rows[rowIndex];
        const lead = row?.dataset ? leadCellOf(row) : undefined;
        if (lead) {
            rootRef.current
                ?.querySelector<HTMLElement>(`[data-tile-id="${lead.id}"]`)
                ?.focus();
        }
    }, [layout]);

    // The panels hang where each says, whatever else the layout does: a
    // row closed, re-hung or rebound, or a host series moving, is followed
    // by placing them again. The placement returns the layout itself once
    // they are in place, so the effect settles at once. The panels are
    // keyed by kind and host series, so a new list with the same hosting
    // does not re-run it.
    const panelHostsKey = panels
        .map((panel) => `${panel.kind}\t${panel.hostSeriesUid ?? ''}`)
        .join('\n');
    const panelHosts = useMemo(
        () =>
            panelHostsKey
                .split('\n')
                .map((entry) => entry.split('\t'))
                .flatMap(([kind, hostSeriesUid]) =>
                    isPanelKind(kind)
                        ? [
                              {
                                  kind,
                                  hostSeriesUid:
                                      seriesInstanceUidOf(hostSeriesUid),
                              },
                          ]
                        : []
                ),
        [panelHostsKey]
    );
    useEffect(() => {
        setLayout((previous) =>
            placePanels(
                previous,
                panelPlacementPlan(previous, panelHosts),
                makeId
            )
        );
    }, [layout, panelHosts, makeId, setLayout]);

    // The layout is the one truth of the grid's shape. The owner learns the
    // shape from it, and asks for another through `hangingProtocol`.
    const hung = useMemo(() => protocolOfLayout(layout), [layout]);
    useEffect(() => {
        onHangingChanged?.(hung);
    }, [hung, onHangingChanged]);

    // The owner hears of a change of the rows' images, not of every resize.
    const rowImages = useMemo(() => imagesOfRows(layout), [layout]);
    const reportedRowImages = useRef<ReadonlyArray<SeriesInstanceUid>>();
    useEffect(() => {
        const reported = reportedRowImages.current;
        if (reported && sameElements(reported, rowImages)) {
            return;
        }
        reportedRowImages.current = rowImages;
        onRowImagesChanged?.(rowImages);
    }, [rowImages, onRowImagesChanged]);

    const requestRowsRef = useRef(binding.requestRows);
    requestRowsRef.current = binding.requestRows;
    const appliedRowsRequest = useRef(imageRowsRequest);
    useEffect(() => {
        if (
            !imageRowsRequest ||
            imageRowsRequest === appliedRowsRequest.current
        ) {
            return;
        }
        appliedRowsRequest.current = imageRowsRequest;
        requestRowsRef.current(imageRowsRequest.seriesInstanceUids);
    }, [imageRowsRequest]);

    // Only a change of the owner's request re-hangs. The request it opened
    // with is already hung, and the request going back to `undefined` after
    // a row hung its own way must leave that row alone.
    const requested = useRef(hangingProtocol);
    useEffect(() => {
        if (hangingProtocol === requested.current) {
            return;
        }
        requested.current = hangingProtocol;
        if (!hangingProtocol) {
            return;
        }
        rememberedShapes.current.clear();
        setLayout((previous) =>
            rehangRows(previous, hangingProtocol.template, makeId)
        );
    }, [hangingProtocol, makeId, setLayout]);

    // An active id outlives its cell when that pane is closed or re-hung.
    // Left in place it would carry the tab stop to a tile that no longer
    // exists, so it clears. A pane of an unbound row is not a tile either.
    const effectiveActiveId =
        activeId && locateViewport(layout, activeId)?.row.dataset
            ? activeId
            : undefined;
    useEffect(() => {
        if (activeId !== undefined && effectiveActiveId === undefined) {
            setActiveId(undefined);
        }
    }, [activeId, effectiveActiveId]);

    // The activated unbound row clears the same way once it is bound or
    // closed, so a row that shows an image is never taken for an empty one.
    const unboundRowActive =
        activeUnboundRow !== undefined &&
        layout.rows[activeUnboundRow] !== undefined &&
        !layout.rows[activeUnboundRow].dataset;
    useEffect(() => {
        if (activeUnboundRow !== undefined && !unboundRowActive) {
            setActiveUnboundRow(undefined);
        }
    }, [activeUnboundRow, unboundRowActive]);

    // A maximized id outlives its pane the same way: its row closed, re-hung
    // or emptied. Left in place it would name a tile no longer on screen, so
    // it clears and the grid shows its rows again.
    const maximizedPane = maximizedId
        ? locateViewport(layout, maximizedId)
        : undefined;
    const effectiveMaximizedId =
        maximizedPane &&
        (isPanelCell(maximizedPane.cell) || maximizedPane.row.dataset)
            ? maximizedId
            : undefined;
    useEffect(() => {
        if (maximizedId !== undefined && effectiveMaximizedId === undefined) {
            setMaximizedId(undefined);
        }
    }, [maximizedId, effectiveMaximizedId]);

    const cycleTargetRowPlanes = useCallback(
        () =>
            cycleRowPlanes(
                planeCycleTarget(
                    layout,
                    effectiveMaximizedId,
                    effectiveActiveId
                )
            ),
        [cycleRowPlanes, layout, effectiveMaximizedId, effectiveActiveId]
    );
    usePlaneCycleShortcut(cycleTargetRowPlanes);

    return (
        <Box ref={rootRef} sx={{ display: 'contents' }}>
            <ViewportGrid
                layout={layout}
                datasetAdapter={datasetAdapter}
                environmentAdapter={environmentAdapter}
                windowingAdapter={windowingAdapter}
                blendAdapter={blendAdapter}
                activeId={effectiveActiveId}
                unboundRowActive={unboundRowActive}
                maximizedId={effectiveMaximizedId}
                scaleBarVisible={scaleBarVisible}
                tileOverlay={tileOverlay}
                tileStatus={tileStatus}
                imageDetails={imageDetails}
                rowOverlayControls={rowOverlayControls}
                panels={panels}
                widgets={widgets}
                editableSeriesUid={editableSeriesUid}
                primaryRowNotice={notice}
                primaryImage={primaryImage}
                canSelectRoi={canSelectRoi}
                onActivate={activatePane}
                onActivateUnboundRow={activateUnboundRow}
                onActiveRendererChanged={onActiveRendererChanged}
                onSelectRoi={onSelectRoi}
                structureActions={structureActions}
                onToggleMaximize={handleToggleMaximize}
                onAddRowBelow={handleAddRowBelow}
                onCloseRow={handleCloseRow}
                onReorder={handleReorder}
                onToggleSideBySide={handleToggleSideBySide}
                onRotateRowPlanes={handleRotateRowPlanes}
                onRehangRow={handleRehangRow}
                onSetRowLock={handleSetRowLock}
                onSetRowFusion={handleSetRowFusion}
                onLayoutChange={setLayout}
                windowLevelTargets={windowLevelTargets}
                binding={rowBinding}
            />
        </Box>
    );
}

export default ViewportGridHost;
